import './styles.scss';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    IconTrash, IconChevronDown, IconPlus, IconX, IconGripVertical,
    IconCalendar, IconCalendarTime, IconLock,
    IconMoodAngry, IconMoodSad, IconMoodNeutral,
    IconMoodSmile, IconMoodHappy, IconMoodCrazyHappy,
    IconThumbUp, IconThumbDown, IconStar,
} from '@tabler/icons-react';
import { Text, Input, Button, ErrorMsg } from '@fragments';
import { QUESTION_TYPES } from '../../index';

let _optId = 0;
const makeOptId = () => `opt-${++_optId}`;

const SCALE_ICONS = {
    angry: IconMoodAngry,
    sad: IconMoodSad,
    neutral: IconMoodNeutral,
    smile: IconMoodSmile,
    happy: IconMoodHappy,
    crazy: IconMoodCrazyHappy,
    thumbup: IconThumbUp,
    thumbdown: IconThumbDown,
    star: IconStar,
};

const SCALE_ICON_ENTRIES = Object.entries(SCALE_ICONS);

const SCALE_COLOR_PALETTE = [
    null, '#111111', '#444444', '#777777', '#aaaaaa', '#dddddd', '#ffffff',
    '#7f0000', '#7f3b00', '#7f6b00', '#1a4d1a', '#00404d', '#003380', '#33006b',
    '#cc0000', '#cc6600', '#aaaa00', '#006600', '#007a7a', '#0055cc', '#6600cc',
    '#ff2222', '#ff8800', '#ddcc00', '#22aa22', '#00bbbb', '#2299ff', '#8822ff',
    '#ff8888', '#ffbb66', '#eeee77', '#88dd66', '#66dddd', '#88ccff', '#bb88ff',
    '#ffcccc', '#ffe0bb', '#ffffaa', '#cceecc', '#bbeeee', '#cce4ff', '#e0ccff',
    '#e91e63', '#ff5722', '#ffc107', '#4caf50', '#00bcd4', '#2196f3', '#9c27b0',
];

function isLightColor(hex) {
    if (!hex) return true;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
}

// ── Editable Label ─────────────────────────────────────────────────────────────

const LABEL_MAX = 50;

function EditableLabel({ value, placeholder, required, onChange, focusOnMount, hasError, errorMsg }) {
    const ref = useRef(null);
    const isComposing = useRef(false);

    useEffect(() => {
        if (!ref.current) return;
        if (document.activeElement === ref.current) return;
        if (ref.current.textContent !== (value ?? '')) {
            ref.current.textContent = value ?? '';
        }
    }, [value]);

    useEffect(() => {
        if (!focusOnMount || !ref.current) return;
        ref.current.focus();
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        range.collapse(false);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInput = (e) => {
        let text = e.currentTarget.textContent;
        if (text.length > LABEL_MAX) {
            text = text.slice(0, LABEL_MAX);
            e.currentTarget.textContent = text;
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            range.collapse(false);
            window.getSelection()?.removeAllRanges();
            window.getSelection()?.addRange(range);
        }
        if (!isComposing.current) onChange(text);
    };

    return (
        <div className={`qcep-label-wrap${hasError ? ' qcep-label-wrap--error' : ''}`}>
            <span
                ref={ref}
                className="qcep-label-input"
                contentEditable
                suppressContentEditableWarning
                data-placeholder={placeholder}
                onInput={handleInput}
                onCompositionStart={() => { isComposing.current = true; }}
                onCompositionEnd={e => {
                    isComposing.current = false;
                    onChange(e.currentTarget.textContent);
                }}
                onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
            />
            {required && <span className="qcp-required">*</span>}
            <ErrorMsg>{hasError ? errorMsg : null}</ErrorMsg>
        </div>
    );
}

// ── Description Field ─────────────────────────────────────────────────────────

const DESC_MAX = 250;

function DescriptionField({ value, onChange, onRemove, autoFocus }) {
    const ref = useRef(null);
    const isComposing = useRef(false);

    useEffect(() => {
        if (!ref.current) return;
        if (document.activeElement === ref.current) return;
        if (ref.current.textContent !== (value ?? '')) {
            ref.current.textContent = value ?? '';
        }
    }, [value]);

    useEffect(() => {
        if (!autoFocus || !ref.current) return;
        ref.current.focus();
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        range.collapse(false);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInput = (e) => {
        let text = e.currentTarget.textContent;
        if (text.length > DESC_MAX) {
            text = text.slice(0, DESC_MAX);
            e.currentTarget.textContent = text;
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            range.collapse(false);
            window.getSelection()?.removeAllRanges();
            window.getSelection()?.addRange(range);
        }
        if (!isComposing.current) onChange(text);
    };

    return (
        <span
            ref={ref}
            className="qcep-desc-input"
            contentEditable
            suppressContentEditableWarning
            data-placeholder="Adicione uma instrução..."
            data-empty={!value}
            onInput={handleInput}
            onCompositionStart={() => { isComposing.current = true; }}
            onCompositionEnd={e => {
                isComposing.current = false;
                onChange(e.currentTarget.textContent);
            }}
            onKeyDown={e => { if (e.key === 'Escape') e.currentTarget.blur(); }}
            onBlur={e => { if (!e.currentTarget.textContent) onRemove(); }}
        />
    );
}

// ── Option Label ──────────────────────────────────────────────────────────────

function OptionLabel({ value, placeholder, onChange, autoFocus }) {
    const ref = useRef(null);
    const isComposing = useRef(false);

    useEffect(() => {
        if (!ref.current) return;
        if (document.activeElement === ref.current) return;
        if (ref.current.textContent !== (value ?? '')) {
            ref.current.textContent = value ?? '';
        }
    }, [value]);

    useEffect(() => {
        if (!autoFocus || !ref.current) return;
        ref.current.focus();
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        range.collapse(false);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <span
            ref={ref}
            className="qcep-option-label"
            contentEditable
            suppressContentEditableWarning
            data-placeholder={placeholder}
            data-empty={!value}
            onInput={e => {
                if (!isComposing.current) onChange(e.currentTarget.textContent);
            }}
            onCompositionStart={() => { isComposing.current = true; }}
            onCompositionEnd={e => {
                isComposing.current = false;
                onChange(e.currentTarget.textContent);
            }}
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
        />
    );
}

// ── Inline Options ────────────────────────────────────────────────────────────

function InlineOptions({ options, onChange, type, settings, onSettingsChange, optionsError }) {
    const initId = options.length === 1 && !options[0]?.label ? options[0].id : null;
    const [lastAddedId, setLastAddedId] = useState(initId);
    const [draggingId, setDraggingId] = useState(null);
    const [liveOptions, setLiveOptions] = useState(null);

    const displayOptions = liveOptions ?? options;

    const addOption = () => {
        const newId = makeOptId();
        onChange([...options, { id: newId, label: '' }]);
        setLastAddedId(newId);
    };

    const updateOption = (id, label) => onChange(options.map(o => o.id === id ? { ...o, label } : o));
    const removeOption = (id) => onChange(options.filter(o => o.id !== id));

    const isMulti = type === 'choices';
    const useRadio = type === 'select' || (isMulti && (settings?.singleSelect ?? false));

    const handleDragStart = (e, id) => {
        setDraggingId(id);
        setLiveOptions([...options]);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, targetId) => {
        e.preventDefault();
        if (!draggingId || draggingId === targetId) return;
        setLiveOptions(prev => {
            const cur = prev ?? options;
            const from = cur.findIndex(o => o.id === draggingId);
            const to = cur.findIndex(o => o.id === targetId);
            if (from === -1 || to === -1) return cur;
            const next = [...cur];
            const [item] = next.splice(from, 1);
            next.splice(to, 0, item);
            return next;
        });
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (liveOptions) onChange(liveOptions);
        setDraggingId(null);
        setLiveOptions(null);
    };

    const handleDragEnd = () => {
        setDraggingId(null);
        setLiveOptions(null);
    };

    return (
        <div className="qcep-options">
            {displayOptions.map((o, i) => (
                <div
                    key={o.id}
                    className={`qcep-option-row${draggingId === o.id ? ' qcep-option-row--dragging' : ''}`}
                    draggable
                    onDragStart={e => handleDragStart(e, o.id)}
                    onDragOver={e => handleDragOver(e, o.id)}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                >
                    <div className="qcep-drag-handle">
                        <IconGripVertical size={12} />
                    </div>
                    <div className="qcep-option-marker">
                        {useRadio ? <span className="qcep-radio" /> : <span className="qcep-checkbox" />}
                    </div>
                    <OptionLabel
                        value={o.label}
                        placeholder={`Opção ${i + 1}`}
                        onChange={label => updateOption(o.id, label)}
                        autoFocus={o.id === lastAddedId}
                    />
                    <button
                        type="button"
                        className="qcep-opt-delete"
                        onMouseDown={e => e.preventDefault()}
                        onClick={e => { e.stopPropagation(); removeOption(o.id); }}
                    >
                        <IconX size={11} />
                    </button>
                </div>
            ))}

            {isMulti && settings?.allowOther && (
                <div className="qcep-option-row qcep-option-row--other">
                    <div className="qcep-option-marker">
                        {useRadio ? <span className="qcep-radio" /> : <span className="qcep-checkbox" />}
                    </div>
                    <span className="qcep-option-label qcep-option-label--other">
                        <Text.span contrast={4}>Outro...</Text.span>
                    </span>
                </div>
            )}

            <Button
                size="small"
                color="overlay"
                startIcon={<IconPlus />}
                onClick={addOption}
                preventLoading
                className="mt-1"
            >
                Adicionar opção
            </Button>

            <ErrorMsg>{optionsError}</ErrorMsg>

            {isMulti && (
                <div className="qcep-switches">
                    <Input.Switch
                        size="small"
                        fullWidth
                        label="Resposta única"
                        value={settings?.singleSelect ?? false}
                        onInput={v => onSettingsChange({ singleSelect: v })}
                    />
                    <Input.Switch
                        size="small"
                        fullWidth
                        label='Permitir "Outro"'
                        value={settings?.allowOther ?? false}
                        onInput={v => onSettingsChange({ allowOther: v })}
                    />
                </div>
            )}
        </div>
    );
}

// ── Scale Dot Popover ─────────────────────────────────────────────────────────

function DotPopover({ cfg, n, patchDot, pos, onClose }) {
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    return createPortal(
        <div
            ref={ref}
            className="qcep-dot-popover"
            style={{ top: pos.top, left: pos.left }}
        >
            <input
                className="qcep-dot-label-input"
                value={cfg.label ?? ''}
                onChange={e => patchDot(n, { label: e.target.value })}
                placeholder={String(n)}
                autoFocus
            />
            <div className="qcep-dot-icons">
                <button
                    type="button"
                    className={`qcep-dot-icon-btn${!cfg.iconKey ? ' qcep-dot-icon-btn--active' : ''}`}
                    onClick={() => patchDot(n, { iconKey: null })}
                    title="Texto"
                >
                    <IconX size={11} />
                </button>
                {SCALE_ICON_ENTRIES.map(([key, Icon]) => (
                    <button
                        type="button"
                        key={key}
                        className={`qcep-dot-icon-btn${cfg.iconKey === key ? ' qcep-dot-icon-btn--active' : ''}`}
                        onClick={() => patchDot(n, { iconKey: key })}
                    >
                        <Icon size={14} />
                    </button>
                ))}
            </div>
            <div className="qcep-dot-colors">
                {SCALE_COLOR_PALETTE.map((c, i) => (
                    <button
                        type="button"
                        key={i}
                        className={`qcep-dot-color${cfg.color === c ? ' qcep-dot-color--active' : ''}${c === null ? ' qcep-dot-color--default' : ''}`}
                        style={c ? { background: c } : undefined}
                        onClick={() => patchDot(n, { color: c })}
                        title={c ?? 'Padrão'}
                    />
                ))}
            </div>
        </div>,
        document.body
    );
}

// ── Scale Editor ──────────────────────────────────────────────────────────────

function ScaleEditor({ settings, onSettingsChange }) {
    const min = settings?.min ?? 1;
    const max = settings?.max ?? 5;
    const steps = Array.from({ length: Math.min(max - min + 1, 10) }, (_, i) => min + i);
    const [openDot, setOpenDot] = useState(null);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

    const getDotCfg = (n) => settings?.dotConfigs?.[n] ?? {};
    const patchDot = (n, patch) => onSettingsChange({
        dotConfigs: { ...(settings?.dotConfigs ?? {}), [n]: { ...getDotCfg(n), ...patch } },
    });

    const handleDotClick = (n, e) => {
        if (openDot === n) { setOpenDot(null); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        const popoverWidth = 190;
        const margin = 8;
        const idealLeft = rect.left + rect.width / 2;
        const clampedLeft = Math.min(
            Math.max(idealLeft, margin + popoverWidth / 2),
            window.innerWidth - margin - popoverWidth / 2,
        );
        setPopoverPos({ top: rect.bottom + 6, left: clampedLeft });
        setOpenDot(n);
    };

    return (
        <div className="qcep-scale">
            <div className="qcep-scale-steppers">
                <div className="qcep-scale-stepper">
                    <Text.span contrast={4}>Mín</Text.span>
                    <button
                        type="button"
                        className="qcep-scale-step-btn"
                        onClick={() => onSettingsChange({ min: Math.min(min + 1, max - 1) })}
                        disabled={min >= max - 1}
                    >+</button>
                    <span className="qcep-scale-step-val">
                        <Text.span contrast={7}>{String(min)}</Text.span>
                    </span>
                    <button
                        type="button"
                        className="qcep-scale-step-btn"
                        onClick={() => onSettingsChange({ min: Math.max(0, min - 1) })}
                        disabled={min <= 0}
                    >−</button>
                </div>

                <div className="qcep-scale-stepper-divider" />

                <div className="qcep-scale-stepper">
                    <Text.span contrast={4}>Máx</Text.span>
                    <button
                        type="button"
                        className="qcep-scale-step-btn"
                        onClick={() => onSettingsChange({ max: Math.min(10, max + 1) })}
                        disabled={max >= 10}
                    >+</button>
                    <span className="qcep-scale-step-val">
                        <Text.span contrast={7}>{String(max)}</Text.span>
                    </span>
                    <button
                        type="button"
                        className="qcep-scale-step-btn"
                        onClick={() => onSettingsChange({ max: Math.max(min + 1, max - 1) })}
                        disabled={max <= min + 1}
                    >−</button>
                </div>
            </div>

            <div className="qcep-scale-dots">
                {steps.map(n => {
                    const cfg = getDotCfg(n);
                    const DotIcon = cfg.iconKey ? SCALE_ICONS[cfg.iconKey] : null;
                    const isOpen = openDot === n;
                    const hasColor = !!cfg.color;
                    const iconColor = hasColor ? (isLightColor(cfg.color) ? '#111111' : '#ffffff') : undefined;
                    const dotStyle = hasColor
                        ? { background: cfg.color, borderColor: cfg.color, color: iconColor }
                        : {};
                    const dotLabel = (cfg.label !== undefined && cfg.label !== '') ? cfg.label : String(n);
                    return (
                        <div key={n} className="qcep-scale-dot-wrap">
                            <button
                                type="button"
                                className={`qcep-scale-dot${isOpen ? ' qcep-scale-dot--open' : ''}`}
                                style={dotStyle}
                                onClick={e => handleDotClick(n, e)}
                            >
                                {DotIcon
                                    ? <DotIcon size={13} color={iconColor} />
                                    : dotLabel
                                }
                            </button>
                        </div>
                    );
                })}
            </div>

            {openDot !== null && (
                <DotPopover
                    cfg={getDotCfg(openDot)}
                    n={openDot}
                    patchDot={patchDot}
                    pos={popoverPos}
                    onClose={() => setOpenDot(null)}
                />
            )}

            <div className="qcep-scale-labels">
                <Input
                    value={settings?.minLabel ?? ''}
                    onInput={v => onSettingsChange({ minLabel: v })}
                    label="Rótulo mínimo"
                    placeholder="Ex: Péssimo"
                />
                <Input
                    value={settings?.maxLabel ?? ''}
                    onInput={v => onSettingsChange({ maxLabel: v })}
                    label="Rótulo máximo"
                    placeholder="Ex: Excelente"
                />
            </div>
        </div>
    );
}

// ── Yes / No / Maybe Editor ───────────────────────────────────────────────────

function YesNoEditor({ settings, onSettingsChange }) {
    const showMaybe = settings?.showMaybe ?? false;
    return (
        <div className="qcep-yes-no">
            <div className="qcep-pill-group">
                <div className="qcep-pill"><Text.span contrast={5}>Sim</Text.span></div>
                <div className="qcep-pill"><Text.span contrast={5}>Não</Text.span></div>
                {showMaybe && (
                    <div className="qcep-pill qcep-pill--maybe">
                        <Text.span contrast={5}>Talvez</Text.span>
                    </div>
                )}
            </div>
            <Input.Switch
                size="small"
                fullWidth
                label='Mostrar "Talvez"'
                value={showMaybe}
                onInput={v => onSettingsChange({ showMaybe: v })}
            />
        </div>
    );
}

// ── Editable Preview Card ─────────────────────────────────────────────────────

function EditablePreviewCard({ question, stepId, qt, updateQuestion, questionErrors, clearError }) {
    const [cardHovered, setCardHovered] = useState(false);
    const [descJustAdded, setDescJustAdded] = useState(false);
    const hoverTimer = useRef(null);

    const update = (fields) => updateQuestion(stepId, question.id, fields);
    const patchSettings = (patch) => updateQuestion(stepId, question.id, {
        settings: { ...(question.settings ?? {}), ...patch },
    });

    const hasDesc = question.description !== null && question.description !== undefined;

    const handleMouseEnter = () => { clearTimeout(hoverTimer.current); setCardHovered(true); };
    const handleMouseLeave = () => { hoverTimer.current = setTimeout(() => setCardHovered(false), 150); };

    const handleAddDesc = () => {
        update({ description: '' });
        setDescJustAdded(true);
    };

    const renderField = () => {
        switch (question.type) {
            case 'select':
            case 'choices':
                return (
                    <InlineOptions
                        options={question.settings?.options ?? []}
                        onChange={options => {
                            patchSettings({ options });
                            clearError?.(['steps', stepId, 'questions', question.id, 'options']);
                        }}
                        type={question.type}
                        settings={question.settings}
                        onSettingsChange={patchSettings}
                        optionsError={questionErrors?.options}
                    />
                );

            case 'scale':
                return <ScaleEditor settings={question.settings} onSettingsChange={patchSettings} />;

            case 'yes_no_maybe':
                return <YesNoEditor settings={question.settings} onSettingsChange={patchSettings} />;

            case 'gender':
                return (
                    <div className="qcep-pill-group">
                        <div className="qcep-pill"><Text.span contrast={5}>Masculino</Text.span></div>
                        <div className="qcep-pill"><Text.span contrast={5}>Feminino</Text.span></div>
                        <div className="qcep-pill"><Text.span contrast={5}>Outro</Text.span></div>
                    </div>
                );

            case 'body_structure':
                return (
                    <div className="qcep-pill-group">
                        <div className="qcep-pill"><Text.span contrast={5}>Ectomorfo</Text.span></div>
                        <div className="qcep-pill"><Text.span contrast={5}>Mesomorfo</Text.span></div>
                        <div className="qcep-pill"><Text.span contrast={5}>Endomorfo</Text.span></div>
                    </div>
                );

            case 'url':
                return (
                    <div className="qcep-field">
                        <Text.span contrast={4} className="qcep-prefix">https://</Text.span>
                        <Text.span contrast={4} className="qcep-placeholder">exemplo.com</Text.span>
                    </div>
                );

            case 'full_name':
                return (
                    <div className="qcep-field">
                        <Text.span contrast={4} className="qcep-placeholder">Nome completo</Text.span>
                    </div>
                );

            case 'email':
                return (
                    <div className="qcep-field">
                        <Text.span contrast={4} className="qcep-placeholder">seuemail@mail.com</Text.span>
                    </div>
                );

            case 'phone':
                return (
                    <div className="qcep-field">
                        <Text.span contrast={4} className="qcep-prefix">+55</Text.span>
                        <Text.span contrast={4} className="qcep-placeholder">(--) 9 ---- ----</Text.span>
                    </div>
                );

            case 'weight':
                return (
                    <div className="qcep-field">
                        <Text.span contrast={4} className="qcep-prefix">kg</Text.span>
                        <Text.span contrast={4} className="qcep-placeholder">Sua resposta</Text.span>
                    </div>
                );

            case 'date':
                return (
                    <div className="qcep-field">
                        <IconCalendar size={14} />
                        <Text.span contrast={4} className="qcep-placeholder">dd/mm/aaaa</Text.span>
                    </div>
                );

            case 'datetime':
                return (
                    <div className="qcep-field">
                        <IconCalendarTime size={14} />
                        <Text.span contrast={4} className="qcep-placeholder">dd/mm/aaaa hh:mm</Text.span>
                    </div>
                );

            case 'photo':
            case 'video':
                return (
                    <div className="qcep-upload qcep-upload--disabled">
                        <IconLock size={14} />
                        <Text.span contrast={4}>Em breve</Text.span>
                    </div>
                );

            default:
                return (
                    <div className="qcep-field">
                        <Text.span contrast={4} className="qcep-placeholder">Sua resposta</Text.span>
                    </div>
                );
        }
    };

    return (
        <div
            className={`qc-editable-preview${cardHovered && !hasDesc ? ' qc-editable-preview--hovered' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <EditableLabel
                value={question.label}
                placeholder={qt?.placeholder ?? 'Título da pergunta'}
                required={question.required}
                onChange={v => {
                    update({ label: v });
                    clearError?.(['steps', stepId, 'questions', question.id, 'label']);
                }}
                focusOnMount={!question.label}
                hasError={!!questionErrors?.label}
                errorMsg={questionErrors?.label}
            />

            {hasDesc ? (
                <DescriptionField
                    value={question.description}
                    autoFocus={descJustAdded}
                    onChange={v => update({ description: v || null })}
                    onRemove={() => { update({ description: null }); setDescJustAdded(false); }}
                />
            ) : (
                <div className="qcep-add-desc-wrap">
                    <Button
                        color="overlay"
                        size="small"
                        fullWidth
                        preventLoading
                        onClick={handleAddDesc}
                    >
                        <Text.span contrast={5}>Adicionar descrição</Text.span>
                    </Button>
                </div>
            )}

            {renderField()}
        </div>
    );
}

// ── QuestionCard ──────────────────────────────────────────────────────────────

export default function QuestionCard({
    question, stepId, expanded, onToggle, updateQuestion, removeQuestion,
    questionErrors, clearError,
    cardDragProps,
}) {
    const qt = QUESTION_TYPES.find(t => t.type === question.type);
    const QIcon = qt?.Icon;
    const cardRef = useRef(null);

    useEffect(() => {
        if (!expanded || !cardRef.current) return;
        const id = setTimeout(() => {
            const card = cardRef.current;
            if (!card) return;
            const scrollContainer = card.closest('.fbrp-body');
            if (!scrollContainer) return;
            const containerRect = scrollContainer.getBoundingClientRect();
            const cardRect = card.getBoundingClientRect();
            const target = scrollContainer.scrollTop + (cardRect.top - containerRect.top) - 40;
            scrollContainer.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
        }, 60);
        return () => clearTimeout(id);
    }, [expanded]);

    const update = (fields) => updateQuestion(stepId, question.id, fields);

    const { 'data-dragging': isDragging, ...restCardDragProps } = cardDragProps ?? {};

    return (
        <div
            ref={cardRef}
            className={`question-card${expanded ? ' question-card--expanded' : ''}${isDragging ? ' question-card--dragging' : ''}`}
            {...restCardDragProps}
        >
            <div className="qc-header" onClick={onToggle}>
                <div className="qc-drag-handle">
                    <IconGripVertical size={14} />
                </div>

                <div className="qc-type-icon">
                    {QIcon && <QIcon size={14} />}
                </div>

                <div className="qc-info">
                    <Text.span contrast={question.label ? 7 : 4}>
                        {question.label || 'Sem título'}
                    </Text.span>
                    <Text.span contrast={4}>{qt?.label ?? question.type}</Text.span>
                </div>

                {!expanded && questionErrors && Object.keys(questionErrors).length > 0 && (
                    <span className="qc-error-dot" title="Esta pergunta tem campos inválidos" />
                )}

                <div className="qc-header-actions">
                    <span className={`qc-chevron${expanded ? ' qc-chevron--up' : ''}`}>
                        <IconChevronDown size={14} />
                    </span>
                    <Button
                        size="small"
                        color="inverse"
                        variant="text"
                        icon={<IconTrash size={12} />}
                        onClick={e => {
                            e.stopPropagation();
                            window.openConfirm({
                                title: 'Remover pergunta',
                                subtitle: `Tem certeza que deseja remover "${question.label || 'esta pergunta'}"?`,
                                buttons: [
                                    { text: 'Remover', variant: 'contained', color: 'error', onClick: () => removeQuestion(stepId, question.id) },
                                    { text: 'Cancelar', variant: 'text' },
                                ],
                            });
                        }}
                        preventLoading
                    />
                </div>
            </div>

            {expanded && (
                <div className="qc-body">
                    <EditablePreviewCard
                        question={question}
                        stepId={stepId}
                        qt={qt}
                        updateQuestion={updateQuestion}
                        questionErrors={questionErrors}
                        clearError={clearError}
                    />

                    <div className="qc-row">
                        <Input.Switch
                            label="Obrigatória"
                            size="small"
                            fullWidth
                            value={question.required ?? true}
                            onInput={v => update({ required: v })}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
